import { useQuery } from "@tanstack/react-query";
import { findAllAttestations } from "../lib/queries/attestations-queries";
import { ksAttestationsQK, ksUserQK } from "../lib/query-keys";
import Title from "../components/ui/Title";
import Box from "../components/ui/Box";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "../components/ui/skeleton";
import { Alert } from "../components/ui/alert";
import AttestationAssuranceRowItem from "../components/attestations/AttestationAssuranceRowItem";
import AttestationCiteoRowItem from "../components/attestations/AttestationCiteoRowItem";
import AttestationSnaRowItem from "../components/attestations/AttestationSnaRowItem";
import { findUser } from "../lib/queries/user-queries";

const AttestationsPage = () => {
  const { data: user } = useQuery({
    queryKey: [ksUserQK],
    queryFn: findUser,
  });
  const { data: attestations, isError } = useQuery({
    queryKey: [ksAttestationsQK],
    queryFn: findAllAttestations,
  });

  if (isError) {
    return <Alert variant={"destructive"}>Une erreur s'est produite.</Alert>;
  }

  return (
    <div className='flex flex-col gap-6'>
      <Title>Attestations</Title>
      <Box>
        <Title order={3} className='mb-5'>
          Assurances apicoles
        </Title>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='min-w-[300px]'>Type</TableHead>
              <TableHead className='min-w-[300px]'>Dates de validité</TableHead>
              <TableHead>Ruches</TableHead>
              <TableHead className='text-right'>Télécharger</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!attestations ? (
              <>
                <TableRow>
                  <TableCell colSpan={4}>
                    <Skeleton className='w-full h-12' />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4}>
                    <Skeleton className='w-full h-12' />
                  </TableCell>
                </TableRow>
              </>
            ) : attestations.assurances.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <span className='mt-3 text-gray-500'>Pas d'attestations à afficher</span>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {attestations.assurances.map((assu) => (
                  <AttestationAssuranceRowItem key={assu.id} assurance={assu} />
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </Box>

      {/* CITEO */}
      <Box>
        <Title order={3} className='mb-5'>
          Éco-contributions
        </Title>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='min-w-[300px]'>Nom</TableHead>
              <TableHead className='min-w-[300px]'>Dates de validité</TableHead>
              <TableHead>Ruches</TableHead>
              <TableHead className='text-right'>Télécharger</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!attestations ? (
              <>
                <TableRow>
                  <TableCell colSpan={4}>
                    <Skeleton className='w-full h-12' />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4}>
                    <Skeleton className='w-full h-12' />
                  </TableCell>
                </TableRow>
              </>
            ) : attestations.citeo.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <span className='mt-3 text-gray-500'>Pas d'attestations à afficher</span>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {attestations.citeo.map((abo) => (
                  <AttestationCiteoRowItem key={abo.id} abonnement={abo} user={user} />
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </Box>

      {/* SNA */}
      <Box>
        <Title order={3} className='mb-5'>
          Adhésions à une structure affiliée au Syndicat National d'Apiculture
        </Title>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='min-w-[300px]'>Nom</TableHead>
              <TableHead className='min-w-[300px]'>Dates de validité</TableHead>
              <TableHead className='text-right'>Télécharger</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!attestations ? (
              <>
                <TableRow>
                  <TableCell colSpan={4}>
                    <Skeleton className='w-full h-12' />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4}>
                    <Skeleton className='w-full h-12' />
                  </TableCell>
                </TableRow>
              </>
            ) : attestations.sna.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <span className='mt-3 text-gray-500'>Pas d'attestations à afficher</span>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {attestations.sna.map((abo) => (
                  <AttestationSnaRowItem key={abo.id} abonnement={abo} />
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </Box>
    </div>
  );
};

export default AttestationsPage;
